import { Injectable, inject } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import {
  Action,
  createSelector,
  NgxsAfterBootstrap,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { OperativeReportsGateway } from '@wilson/api/gateway';
import { OperativeReportsCategorySettings } from '@wilson/operative-reports/interfaces';
import { catchError, Observable, of, tap } from 'rxjs';
import { InitializeOperativeReportCategorySettingsState } from './operative-reports.actions';

export interface OperativeReportCategorySettingsStateModel {
  settings: OperativeReportsCategorySettings[];
}

const STATE_KEY = 'operativeReportCategorySettings';

const defaults: OperativeReportCategorySettingsStateModel = {
  settings: [],
};

/**
 * Offline-first cache of the per-org operative-report category toggles
 * (e.g. is "Activity start" enabled). Mobile-only — mirrors
 * `ActivityPropertySettingsState`'s storage-hydration pattern so a fetch
 * failure (offline) falls back to the last known settings instead of
 * breaking whatever combined it with local-only state.
 */
@State<OperativeReportCategorySettingsStateModel>({
  name: STATE_KEY,
  defaults,
})
@Injectable()
export class OperativeReportCategorySettingsState
  implements NgxsAfterBootstrap
{
  private readonly gateway = inject(OperativeReportsGateway);
  private readonly storage = inject(Storage);

  @Selector()
  static settings(
    state: OperativeReportCategorySettingsStateModel,
  ): OperativeReportsCategorySettings[] {
    return state.settings;
  }

  static isEnabled(
    operativeReportCategoryId: string,
  ): (state: OperativeReportCategorySettingsStateModel) => boolean {
    return createSelector(
      [OperativeReportCategorySettingsState],
      (state: OperativeReportCategorySettingsStateModel) =>
        state.settings.find(
          (setting) =>
            setting.operativeReportCategoryId === operativeReportCategoryId,
        )?.isEnabled ?? false,
    );
  }

  async ngxsAfterBootstrap(
    ctx: StateContext<OperativeReportCategorySettingsStateModel>,
  ): Promise<void> {
    const stored: OperativeReportsCategorySettings[] | null =
      await this.storage.get(STATE_KEY);
    if (stored?.length) {
      ctx.patchState({ settings: stored });
    }
  }

  @Action(InitializeOperativeReportCategorySettingsState)
  initialize(
    ctx: StateContext<OperativeReportCategorySettingsStateModel>,
  ): Observable<OperativeReportsCategorySettings[] | null> {
    return this.gateway.getOperativeReportsCategoriesSettings().pipe(
      tap((settings) => {
        ctx.patchState({ settings });
        this.storage.set(STATE_KEY, settings);
      }),
      // Offline/network failure — keep whatever was hydrated from storage
      // (or the empty default) rather than erroring the caller.
      catchError(() => of(null)),
    );
  }
}
